import type {
  GpCategory,
  GpCategoryId,
  GpGap,
  GpKpi,
  GpPriority,
  GpPrompt,
  GpReviewPhoto,
  GpReviewStatus,
  GpReviewSummary,
  GpSyncedCategory,
} from '@elva/ui';
import {
  growthDelete,
  growthGet,
  growthList,
  growthPatch,
  growthPost,
  growthPostAsync,
  growthPostFormData,
} from '@/data/http';
import { deepCamel, num, stableNumericId, str } from '@/data/adapt';

const CATEGORY_LABELS: Record<GpCategoryId, string> = {
  essential: 'Essential',
  interior: 'Interior',
  team: 'Team',
  equipment: 'Equipment',
  procedures: 'Procedures',
  beforeafter: 'Before & After',
};

const VALID_CATEGORIES = new Set<string>([
  'essential',
  'interior',
  'team',
  'equipment',
  'procedures',
  'beforeafter',
]);

export interface GbpPhotosData {
  categories: GpCategory[];
  prompts: GpPrompt[];
  kpis: GpKpi[];
  photoIdByPromptId: Record<string, string>;
}

export interface GbpUploadSlot {
  id: string;
  patchId: number;
}

export interface GbpUploadSessionResult {
  sessionId: string;
  status: string;
  slots: GbpUploadSlot[];
}

export interface GbpUploadSessionReview {
  sessionId: string;
  status: string;
  photosTotal: number;
  photosProcessed: number;
  summary: GpReviewSummary;
  slots: GpReviewPhoto[];
  isProcessing: boolean;
  isReadyForReview: boolean;
}

function mapCategoryId(value: string): GpCategoryId {
  const key = value.toLowerCase().replace(/-/g, '') as GpCategoryId;
  if (VALID_CATEGORIES.has(key)) return key;
  return 'essential';
}

function mapGap(value: string): GpGap {
  return value.toLowerCase() === 'high' ? 'high' : 'low';
}

function mapPriority(value: string): GpPriority {
  return value.toLowerCase() === 'essential' ? 'essential' : 'recommended';
}

function formatCategoryLabel(id: string): string {
  const key = mapCategoryId(id);
  return CATEGORY_LABELS[key] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

function bareNumericId(id: string | number): number {
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  const match = String(id).match(/(\d+)/);
  return match ? Number(match[1]) : stableNumericId(id);
}

function normalizeSessionId(id: string): string {
  const trimmed = id.trim();
  if (/^\d+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/(\d+)/);
  return match ? match[1]! : trimmed;
}

function mapSlotStatus(status: string): GpReviewStatus {
  const wire = status.toLowerCase();
  if (wire === 'ready') return 'ready';
  if (wire === 'rejected' || wire === 'failed') return 'rejected';
  if (wire === 'uncategorized') return 'uncategorized';
  return 'warning';
}

function readUploadSessionSlotRows(data: Record<string, unknown>): Record<string, unknown>[] {
  const reviewSlots = (data.slots as Record<string, unknown>[] | undefined) ?? [];
  const uploadSlots = (data.uploadSlots as Record<string, unknown>[] | undefined) ?? [];

  if (reviewSlots.length === 0) return uploadSlots;
  if (uploadSlots.length === 0) return reviewSlots;

  const previewBySlotId = new Map<string, string>();
  for (const row of uploadSlots) {
    const slot = deepCamel<Record<string, unknown>>(row);
    const previewUrl = readSlotPreviewUrl(slot);
    if (!previewUrl) continue;
    previewBySlotId.set(String(slot.id ?? ''), previewUrl);
  }

  return reviewSlots.map((row) => {
    const previewUrl = readSlotPreviewUrl(deepCamel<Record<string, unknown>>(row));
    if (previewUrl) return row;
    const merged = previewBySlotId.get(String(row.id ?? ''));
    return merged ? { ...row, view_url: merged } : row;
  });
}

function readSlotPreviewUrl(slot: Record<string, unknown>): string | undefined {
  const viewUrl = str(slot.viewUrl ?? slot.view_url, '');
  if (viewUrl && viewUrl !== '—') return viewUrl;
  const signedUrl = str(slot.signedUrl ?? slot.signed_url, '');
  if (signedUrl && signedUrl !== '—') return signedUrl;
  return undefined;
}

function adaptCategory(row: Record<string, unknown>): GpCategory {
  const c = deepCamel<Record<string, unknown>>(row);
  const id = mapCategoryId(str(c.id));
  return {
    id,
    label: str(c.label, formatCategoryLabel(str(c.id))),
    count: num(c.count),
    uploaded: num(c.uploaded),
    additional: num(c.additional),
    gap: mapGap(str(c.gap, 'low')),
  };
}

function adaptPrompt(row: Record<string, unknown>): GpPrompt {
  const c = deepCamel<Record<string, unknown>>(row);
  const tips = Array.isArray(c.tips) ? c.tips.map((tip) => str(tip)).filter(Boolean) : [];
  return {
    id: str(c.id),
    category: mapCategoryId(str(c.category)),
    title: str(c.title),
    priority: mapPriority(str(c.priority, 'recommended')),
    description: str(c.description),
    tips,
    example: str(c.example, 'example'),
    uploaded: Boolean(c.uploaded),
    uploadedDays: c.uploadedDays == null ? null : num(c.uploadedDays),
    dimensions: str(c.dimensions, 'Landscape · 1920x1080 recommended'),
    ...(c.requiresConsent ? { requiresConsent: true } : {}),
  };
}

interface BePhotoRow {
  id?: string | number;
  prompt_id?: string;
  category?: string;
  stale?: boolean;
  signed_url?: string | null;
  created_at?: string;
}

function daysSinceIso(iso: string | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.max(0, Math.round((Date.now() - then) / 86_400_000));
}

function mergePhotosIntoPrompts(
  prompts: GpPrompt[],
  photos: BePhotoRow[],
): { prompts: GpPrompt[]; photoIdByPromptId: Record<string, string> } {
  const byPrompt = new Map<string, BePhotoRow>();
  for (const photo of photos) {
    const promptId = str(photo.prompt_id, '');
    if (promptId) byPrompt.set(promptId, photo);
  }

  const photoIdByPromptId: Record<string, string> = {};
  const merged = prompts.map((prompt) => {
    const photo = byPrompt.get(prompt.id);
    if (!photo) return prompt;
    const photoId = String(photo.id ?? '');
    if (photoId) photoIdByPromptId[prompt.id] = photoId;
    const signedUrl = str(photo.signed_url, '');
    const uploadedDays = daysSinceIso(photo.created_at) ?? prompt.uploadedDays;
    return {
      ...prompt,
      uploaded: true,
      uploadedDays,
      ...(signedUrl && signedUrl !== '—' ? { imageUrl: signedUrl } : {}),
      ...(photoId ? { photoId } : {}),
    };
  });

  return { prompts: merged, photoIdByPromptId };
}

function buildKpis(categories: GpCategory[], photoCount: number): GpKpi[] {
  const promptTotal = categories.reduce((sum, cat) => sum + cat.count, 0);
  const uploaded = categories.reduce((sum, cat) => sum + cat.uploaded, 0);
  const additional = categories.reduce((sum, cat) => sum + cat.additional, 0);
  const highGap = categories.filter((cat) => cat.gap === 'high').length;
  const pct = promptTotal > 0 ? Math.round((uploaded / promptTotal) * 100) : 0;

  return [
    {
      key: 'coverage',
      label: 'Prompt Coverage',
      value: String(uploaded),
      unit: `/ ${promptTotal}`,
      sub: `${pct}% of recommended prompts filled`,
      tone: pct >= 80 ? 'success' : pct >= 50 ? 'gold' : 'terra',
    },
    {
      key: 'total',
      label: 'Total GBP Photos',
      value: String(photoCount),
      sub: `${additional} additional beyond prompts`,
      tone: 'neutral',
    },
    {
      key: 'gaps',
      label: 'High-gap Categories',
      value: String(highGap),
      sub: highGap > 0 ? 'Categories needing more photos' : 'All categories on track',
      tone: highGap > 0 ? 'gold' : 'success',
    },
    {
      key: 'freshness',
      label: 'Additional Photos',
      value: String(additional),
      sub: 'Extra images beyond curated prompts',
      tone: additional > 0 ? 'info' : 'neutral',
    },
  ];
}

function buildCategoriesFromPrompts(prompts: GpPrompt[]): GpCategory[] {
  const groups = new Map<GpCategoryId, { count: number; uploaded: number }>();
  for (const prompt of prompts) {
    const group = groups.get(prompt.category) ?? { count: 0, uploaded: 0 };
    group.count += 1;
    if (prompt.uploaded) group.uploaded += 1;
    groups.set(prompt.category, group);
  }
  return Array.from(groups.entries()).map(([id, stats]) => ({
    id,
    label: CATEGORY_LABELS[id],
    count: stats.count,
    uploaded: stats.uploaded,
    additional: 0,
    gap: stats.uploaded < stats.count ? 'high' : 'low',
  }));
}

export async function fetchGbpPhotoPrompts(): Promise<{
  categories: GpCategory[];
  prompts: GpPrompt[];
}> {
  const data = await growthGet<{
    categories?: Array<Record<string, unknown>>;
    prompts?: Array<Record<string, unknown>>;
  }>('/gbp/photos/prompts/').catch(() => ({ categories: [], prompts: [] }));

  return {
    categories: (data.categories ?? []).map(adaptCategory),
    prompts: (data.prompts ?? []).map(adaptPrompt),
  };
}

export async function fetchGbpPhotos(filters?: {
  category?: string;
  stale?: boolean;
}): Promise<GbpPhotosData> {
  const params: Record<string, string | number | boolean> = { limit: 200 };
  if (filters?.category) params.category = filters.category;
  if (filters?.stale != null) params.stale = filters.stale;

  const [catalog, list] = await Promise.all([
    fetchGbpPhotoPrompts(),
    growthList<BePhotoRow>('/gbp/photos/', params).catch(() => ({ items: [] as BePhotoRow[] })),
  ]);

  const { prompts, photoIdByPromptId } = mergePhotosIntoPrompts(catalog.prompts, list.items);
  const categories =
    catalog.categories.length > 0 ? catalog.categories : buildCategoriesFromPrompts(prompts);

  return {
    categories,
    prompts,
    kpis: buildKpis(categories, list.items.length),
    photoIdByPromptId,
  };
}

export async function deleteGbpPhoto(photoId: string) {
  return growthDelete(`/gbp/photos/${encodeURIComponent(photoId)}/`);
}

function adaptUploadSession(raw: Record<string, unknown>): GbpUploadSessionResult {
  const data = deepCamel<Record<string, unknown>>(raw);
  const sessionId = normalizeSessionId(str(data.sessionId ?? data.id, ''));
  const status = str(data.status, '').toLowerCase();
  const uploadSlots = (data.uploadSlots as Record<string, unknown>[] | undefined) ?? [];
  const slots = uploadSlots.map((slot) => {
    const row = deepCamel<Record<string, unknown>>(slot);
    const id = str(row.id, 'slot');
    return {
      id,
      patchId: bareNumericId(id),
    };
  });
  return { sessionId, status, slots };
}

function buildGbpUploadSessionFormData(
  files: File[],
  options?: { prompt_id?: string },
): FormData {
  const formData = new FormData();
  formData.append('slots_requested', String(files.length));
  if (options?.prompt_id) {
    formData.append('prompt_id', options.prompt_id);
  }
  for (const file of files) {
    formData.append('files', file, file.name);
  }
  return formData;
}

export async function createGbpUploadSession(body?: {
  slots_requested?: number;
  prompt_id?: string;
  files?: File[];
}) {
  if (body?.files?.length) {
    return growthPostFormData<Record<string, unknown>>(
      '/gbp/photos/upload-sessions/',
      buildGbpUploadSessionFormData(body.files, {
        ...(body.prompt_id ? { prompt_id: body.prompt_id } : {}),
      }),
    );
  }

  return growthPost<Record<string, unknown>>('/gbp/photos/upload-sessions/', {
    slots_requested: body?.slots_requested ?? 12,
    ...(body?.prompt_id ? { prompt_id: body.prompt_id } : {}),
  });
}

export async function finalizeGbpUploadSession(id: string) {
  const sessionId = normalizeSessionId(id);
  return growthPostAsync(`/gbp/photos/upload-sessions/${sessionId}/finalize/`);
}

export async function cancelGbpUploadSession(id: string) {
  const sessionId = normalizeSessionId(id);
  return growthPost(`/gbp/photos/upload-sessions/${sessionId}/cancel/`);
}

function adaptUploadSessionReview(raw: Record<string, unknown>): GbpUploadSessionReview {
  const data = deepCamel<Record<string, unknown>>(raw);
  const sessionId = normalizeSessionId(str(data.sessionId ?? data.id, ''));
  const status = str(data.status, 'processing').toLowerCase();
  const photosTotal = num(data.photosTotal);
  const photosProcessed = num(data.photosProcessed);
  const summaryRaw = deepCamel<Record<string, unknown>>((data.summary as Record<string, unknown>) ?? {});

  const summary: GpReviewSummary = {
    uploaded:
      photosTotal ||
      num(summaryRaw.autoReady) +
        num(summaryRaw.needReview) +
        num(summaryRaw.uncategorized) +
        num(summaryRaw.rejected),
    autoReady: num(summaryRaw.autoReady),
    needReview: num(summaryRaw.needReview),
    uncategorized: num(summaryRaw.uncategorized),
    rejected: num(summaryRaw.rejected),
  };

  const slotRows = readUploadSessionSlotRows(data);
  const slots: GpReviewPhoto[] = slotRows.map((row, index) => {
    const slot = deepCamel<Record<string, unknown>>(row);
    const patchId = bareNumericId(slot.id as string | number);
    const uiId = stableNumericId(`${patchId}-${index}`);
    const confidenceRaw = num(slot.classificationConfidence);
    const confidence =
      confidenceRaw > 0 && confidenceRaw <= 1
        ? Math.round(confidenceRaw * 100)
        : Math.round(confidenceRaw);
    const matchedPrompt = str(slot.matchedPrompt, '');
    const category = str(slot.classifiedCategory, '');
    const previewUrl = readSlotPreviewUrl(slot);
    return {
      id: uiId,
      patchId,
      example: 'photo',
      fileName: str(slot.fileName, `slot_${slot.slotNumber ?? index + 1}.jpg`),
      size: str(slot.size, '—'),
      category: category && category !== '—' ? category : null,
      matchedPrompt: matchedPrompt && matchedPrompt !== '—' ? matchedPrompt : null,
      confidence,
      description: str(slot.description, ''),
      status: mapSlotStatus(str(slot.status, 'warning')),
      additional: !matchedPrompt,
      ...(previewUrl ? { previewUrl } : {}),
      ...(slot.qualityIssue ? { qualityIssue: str(slot.qualityIssue) } : {}),
      ...(slot.requiresConsent ? { requiresConsent: true } : {}),
    };
  });

  const isReadyForReview =
    ['finalized', 'classified', 'ready', 'complete'].includes(status) ||
    (photosTotal > 0 && photosProcessed >= photosTotal);
  const isProcessing = !isReadyForReview && status !== 'failed' && status !== 'cancelled';

  return {
    sessionId,
    status,
    photosTotal,
    photosProcessed,
    summary,
    slots,
    isProcessing,
    isReadyForReview,
  };
}

export async function fetchGbpUploadSession(id: string): Promise<GbpUploadSessionReview | undefined> {
  const sessionId = normalizeSessionId(id);
  const raw = await growthGet<Record<string, unknown>>(`/gbp/photos/upload-sessions/${sessionId}/`).catch(
    () => undefined,
  );
  if (!raw) return undefined;
  return adaptUploadSessionReview(raw);
}

export async function patchGbpPhotoSlot(
  slotId: number,
  body: { classified_category?: string; description?: string },
) {
  return growthPatch(`/gbp/photos/${slotId}/`, {
    ...(body.classified_category ? { classified_category: body.classified_category } : {}),
    ...(body.description ? { description: body.description } : {}),
  });
}

export async function deleteGbpPhotoSlot(slotId: number) {
  return growthDelete(`/gbp/photos/${slotId}/`);
}

export async function syncGbpPhotoSlots(sessionId: string, slotIds: number[]) {
  const id = normalizeSessionId(sessionId);
  return growthPost(`/gbp/photos/upload-sessions/${id}/sync-selected/`, {
    slot_ids: slotIds,
  });
}

export async function uploadGbpPhotoFiles(
  files: FileList | File[],
  options?: { prompt_id?: string },
): Promise<string> {
  const fileList = Array.from(files).slice(0, 50);
  if (fileList.length === 0) {
    throw new Error('No files selected');
  }

  const session = adaptUploadSession(
    await createGbpUploadSession({
      files: fileList,
      ...(options?.prompt_id ? { prompt_id: options.prompt_id } : {}),
    }),
  );

  if (!session.sessionId) {
    throw new Error('Upload session missing id');
  }

  if (session.slots.length < fileList.length) {
    throw new Error('Upload session returned fewer slots than files');
  }

  await finalizeGbpUploadSession(session.sessionId);
  return session.sessionId;
}

export function syncedCategoriesFromSlots(slots: GpReviewPhoto[]): GpSyncedCategory[] {
  const counts = new Map<string, number>();
  for (const slot of slots) {
    if (!slot.category) continue;
    const label = formatCategoryLabel(slot.category);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
}

export type { GpGap };
